// packages/components/src/app/Footer.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Logo } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import type { IconName } from '@dopomogai/ui';
import type { CmsComponentDocument } from '@dopomogai/types';

// --- Internal Data Types (matching the DB) ---
interface FooterLink { id: string; textKey: string; path: string; }
interface LinkGroup { id: string; titleKey: string; links: FooterLink[]; }
interface SocialLink { id: string; name: string; url: string; }

// --- Component Props ---
export interface FooterProps {
  onAction: (actionType: string, payload?: any) => void;
  data?: CmsComponentDocument;
}

// --- Sub-component for Social Links ---
const SocialLinkWithIcon: React.FC<{ link: SocialLink & { icon: IconName } }> = ({ link }) => (
    <Tooltip text={`Follow us on ${link.name}`} position="top">
        <a
            href={link.url}
            aria-label={`Follow us on ${link.name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-white transition-colors duration-200"
        >
            {renderIcon(link.icon, { size: 20 })}
        </a>
    </Tooltip>
);

// --- Main Footer Component ---
export const Footer: React.FC<FooterProps> = ({ onAction, data }) => {
  const { i18n } = useTranslation('footer');

  // --- SKELETON LOADER ---
  if (!data || !data.structure || !data.locales) {
    return <footer className="bg-gray-900  z-10  border-t" />;
  }
  
  // --- DATA PROCESSING ---
  const textProps = data.locales[i18n.language]?.props || data.locales['en']?.props || {};
  const structuralProps = data.structure.root.props;

  const linkGroups: LinkGroup[] = structuralProps.linkGroups || [];
  const socialLinks: SocialLink[] = structuralProps.socialLinks || [];
  const copyrightKey = structuralProps.copyrightKey || 'copyright';

  const copyrightText = textProps[copyrightKey]?.replace('{{year}}', new Date().getFullYear().toString()) || '';
  
  const getIconForSocial = (name: string): IconName => {
      const normalized = name.toLowerCase();
      if (normalized === 'linkedin') return 'linkedin';
      if (normalized === 'github') return 'github';
      if (normalized === 'twitter') return 'twitter';
      if (normalized === 'x') return 'x';
      return 'globe';
  };

  const handleNavigate = (path: string) => {
    onAction('NAVIGATE_TO_PAGE', { path });
  };

  return (
    <footer className="bg-gray-900 text-slate-300 border-t border-white/10 mt-auto">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          <div className="col-span-1 lg:col-span-1">
            <div className="mb-4">
              <Logo size="md" logoType="logo+icon" className="text-white" />
            </div>
            <div className="flex items-center gap-5 mt-6">
              {socialLinks.map(social => (
                <SocialLinkWithIcon 
                  key={social.id} 
                  link={{ ...social, icon: getIconForSocial(social.name) }} 
                />
              ))}
            </div>
          </div>

          <div className="col-span-1 lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {linkGroups.map(group => (
               <div key={group.id}>
                  <h3 className="text-sm font-semibold text-white tracking-wider uppercase mb-4">{textProps[group.titleKey] || group.titleKey}</h3>
                  <ul className="space-y-3">
                    {group.links.map(link => (
                      <li key={link.id}>
                        <button 
                          onClick={() => handleNavigate(link.path)}
                          className="text-slate-400 hover:text-sky-400 transition-colors duration-200 text-left"
                        >
                          {textProps[link.textKey] || link.textKey}
                        </button>
                      </li>
                    ))}
                  </ul>
               </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-center text-sm text-slate-500">
          <p>{copyrightText}</p>
        </div>
      </div>
    </footer>
  );
};
